import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { User } from '../../../../models/user/user';
import { AlertService } from '../../../../services/alert.service';
import { RegexService } from '../../../../services/regex.service';
import { ToolsService } from '../../../../services/tools.service';
import { UserService } from '../../../../services/user.service';
import { Store } from '../../../../store';
import { checkPasswords } from '../../../../utils/custom-validators';

@Component({
    selector: 'app-registration-form',
    templateUrl: './registration-form.component.html',
    styleUrls: ['./registration-form.component.scss'],
})
export class RegistrationFormComponent implements OnInit, OnDestroy {
    @Input() userFromStorage: User;
    @Input() referralCode;
    @Input() regPromoCode;
    @Input() isBasketAuth = false;
    @Output() registrationComplete = new EventEmitter();
    @Output() switchToSignIn = new EventEmitter();

    overlays$ = this.store.select<any>('overlays');

    emailExists = true;

    form: FormGroup;

    validReferralCode = true;
    userCreated = false;

    newUserId = '';
    newContactId = '';
    newCompanyId = '';
    newAddressId = '';

    user = {
        id: this.newUserId,
        sessionId: '',
        username: '',
        password: '',
        extendedExpiration: false,
        accountType: 'customer',
        terms: false,
        marketing: false,
        referralCode: '',
        regPromoCode: '',
        contact: {
            id: this.newContactId,
            userId: this.newUserId,
            addressId: '',
            companyId: '',
            firstname: '',
            surname: '',
            email: '',
            mobile: '',
            mobDialCode: '',
            landline: '',
            jobTitle: '',
            avatar: '',
        },
        source: '',
    };

    company: any = {
        id: this.newCompanyId,
        primaryUserId: this.newUserId,
        accountType: '',
        addressId: '',
        name: '',
        url: '',
        email: '',
        mobile: '',
        landline: '',
        companyNumber: '',
        vatNumber: '',
        restrictions: '',
    };

    address: any = {
        id: this.newAddressId,
        userId: this.newUserId,
        add1: '',
        add2: '',
        add3: '',
        town: '',
        county: '',
        country: '',
        postcode: '',
        defaultDeliveryAddress: true,
        isBilling: true,
    };

    private _subscriptions: Subscription = new Subscription();

    constructor(
        public regexService: RegexService,
        private store: Store,
        private fb: FormBuilder,
        private userService: UserService,
        private alertService: AlertService,
        private toolsService: ToolsService
    ) {
        this.form = this.fb.group(
            {
                accountType: ['customer', [Validators.required]],

                password: [
                    '',
                    [
                        Validators.compose([
                            this.regexService.patternValidator(/\d/, { hasNumber: true }),
                            this.regexService.patternValidator(/[A-Z]/, { hasCapitalCase: true }),
                            this.regexService.patternValidator(/[a-z]/, { hasSmallCase: true }),
                            this.regexService.patternValidator(/[ !@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/, {
                                hasSpecialCharacters: true,
                            }),
                            Validators.minLength(8),
                            Validators.required,
                            this.regexService.validatePasswordField(),
                        ]),
                    ],
                ],
                passwordConfirm: ['', [Validators.required]],
                terms: [false, [Validators.requiredTrue]],
                marketing: [false, []],
                contact: fb.group({
                    firstname: ['', [Validators.required, Validators.minLength(2)]],
                    surname: ['', [Validators.required, Validators.minLength(2)]],
                    email: ['', [Validators.required, Validators.email]],
                    emailConfirm: ['', []],
                    mobile: ['', [Validators.required]],
                    landline: ['', [Validators.minLength(7)]],
                    jobTitle: ['', [Validators.minLength(2)]],
                }),
                address: fb.group({
                    add1: ['', [Validators.minLength(2)]],
                    add2: ['', [Validators.minLength(2)]],
                    add3: ['', [Validators.minLength(2)]],
                    town: ['', [Validators.minLength(2)]],
                    county: ['', [Validators.minLength(2)]],
                    postcode: ['', [Validators.minLength(2)]],
                }),
                company: fb.group({
                    name: ['', [Validators.minLength(2)]],
                    url: ['', [Validators.minLength(2)]],
                    email: ['', [Validators.email]],
                    mobile: ['', [Validators.minLength(7)]],
                    landline: ['', [Validators.minLength(7)]],
                    companyNumber: ['', [Validators.minLength(2)]],
                    vatNumber: ['', [Validators.minLength(2)]],
                }),
            },
            {
                validators: [checkPasswords()],
            }
        );
    }

    ngOnInit(): void {
        this.createPrimaryIds();
        this._subscriptions.add(
            (this.form.controls.contact as FormGroup).controls.email.valueChanges.subscribe(() => {
                if (this.isEmailExist()) {
                    (this.form.controls.contact as FormGroup).controls.email.setErrors({ emailExists: null });
                    this.form.updateValueAndValidity();
                }
            })
        );
    }
    isEmailExist() {
        return (this.form.controls.contact as FormGroup).controls.email.hasError('emailExists');
    }
    ngOnDestroy(): void {
        this._subscriptions.unsubscribe();
    }

    checkReferralCode() {
        if (this.form.value.email !== '' && this.referralCode !== '') {
            this._subscriptions.add(
                this.userService
                    .checkReferralCode(this.referralCode, this.form.value.contact.email)
                    .subscribe((data) => {
                        this.validReferralCode = data.data.isValid;
                    })
            );
        }
    }

    toggleValue(v) {
        this.user[v] = !this.user[v];
    }

    onSubmit() {
        if (this.form.valid && !this.store.selectForLocal('overlays').busyIndicator && !this.userCreated) {
            this.user.password = this.form.value.password;
            this.user.username = this.form.value.contact.email;

            this.user.contact.firstname = this.form.value.contact.firstname;
            this.user.contact.surname = this.form.value.contact.surname;
            this.user.contact.email = this.form.value.contact.email;
            this.user.contact.mobile = this.form.value.contact.mobile.e164Number; // this.form.value.contact.mobile.number;
            this.user.contact.mobDialCode = this.form.value.contact.mobile.dialCode;
            this.user.contact.landline = this.form.value.contact.landline;
            this.user.contact.jobTitle = this.form.value.contact.jobTitle;
            this.user.terms = this.form.value.terms;

            if (this.referralCode && this.validReferralCode) {
                this.user.referralCode = this.referralCode;
            }
            this.user.regPromoCode = this.regPromoCode;

            this.alertService.clearMessage();
            this.store.set('confirmationEmail', this.user.contact.email);
            this.store.set('accountType', this.user.accountType);
            this.save();
        } else {
            this.form.markAllAsTouched();
        }
    }

    save() {
        if (this.user.accountType !== 'customer') {
            this.user.contact.addressId = '';
            this.user.contact.companyId = this.newCompanyId;
        }

        this.user.sessionId = localStorage.getItem('tmpSessionId');
        this.user.source = 'public';
        this._subscriptions.add(
            this.userService.createUser(this.user).subscribe(
                async (userData) => {
                    this.user.id = userData.data.id;
                    this.userCreated = true;
                    if (this.user.accountType !== 'customer') {
                        this.userService.createCompany(this.company, this.user).subscribe(
                            (data) => {
                                this.company.id = data.data.id;
                                this.registrationComplete.emit({
                                    user: this.user,
                                    company: this.company,
                                });
                            },
                            (error) => {
                                this.alertService.error([error.error.message]);
                            }
                        );
                    } else {
                        this.registrationComplete.emit({
                            user: this.user,
                            company: undefined,
                        });
                    }
                },
                (error) => {
                    if (error?.error?.message === 'User already created with this email') {
                        (this.form.controls.contact as FormGroup).controls.email.setErrors({
                            emailExists: true,
                            required: false,
                        });
                        this.form.updateValueAndValidity();
                    } else {
                        this.alertService.error([error.error.message]);
                    }
                }
            )
        );
    }

    createPrimaryIds() {
        this.newUserId = this.toolsService.newUUID();
        this.newContactId = this.toolsService.newUUID();
        this.newAddressId = this.toolsService.newUUID();
        this.newCompanyId = this.toolsService.newUUID();

        this.user.id = this.newUserId;
        this.user.contact.id = this.newContactId;
        this.user.contact.userId = this.newUserId;

        this.company.id = this.newCompanyId;
        this.company.primaryUserId = this.newUserId;
        this.address.id = this.newAddressId;
    }

    doSignIn() {
        this.switchToSignIn.emit();
    }
}
